import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { 
    RefreshCw, 
    Clock, 
    Calendar,
    CheckCircle2,
    XCircle,
    Loader2,
    Settings,
    Play,
    AlertCircle,
    ChevronDown,
    ChevronUp,
    Copy,
    X,
    Wrench
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

export default function AdminScraperPage() {
    const { token } = useAuth();
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [runningManual, setRunningManual] = useState(false);
    const [showErrorDetails, setShowErrorDetails] = useState(false);
    const [fixingBrowser, setFixingBrowser] = useState(false);
    
    const [intervalHours, setIntervalHours] = useState(24);
    const [enabled, setEnabled] = useState(true);

    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

    useEffect(() => {
        fetchSettings();
        // Poll for status updates every 10 seconds
        const interval = setInterval(fetchSettings, 10000);
        return () => clearInterval(interval);
    }, []);

    const fetchSettings = async () => {
        try {
            const response = await fetch(`${API}/admin/scraper/settings`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setSettings(data);
                setIntervalHours(data.interval_hours);
                setEnabled(data.enabled);
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const response = await fetch(`${API}/admin/scraper/settings`, {
                method: 'PUT',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    interval_hours: intervalHours,
                    enabled
                })
            });
            
            if (response.ok) {
                const data = await response.json();
                setSettings(data);
                toast.success('Settings saved successfully');
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Failed to save settings');
            }
        } catch (error) {
            toast.error('Error saving settings');
        } finally {
            setSaving(false);
        }
    };

    const handleRunNow = async () => {
        setRunningManual(true);
        try {
            const response = await fetch(`${API}/admin/scraper/run-now`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (response.ok) {
                toast.success('Scraper started');
                // Poll more frequently while running
                setTimeout(fetchSettings, 2000);
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Failed to start scraper');
            }
        } catch (error) {
            toast.error('Error starting scraper');
        } finally {
            setRunningManual(false);
        }
    };

    const handleFixBrowser = async () => {
        setFixingBrowser(true);
        toast.info('Reinstalling browser... This may take a minute.');
        
        try {
            const response = await fetch(`${API}/admin/scraper/fix-browser`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            const result = await response.json();
            
            if (result.success) {
                toast.success(result.message || 'Browser fixed successfully!');
                setShowErrorDetails(false);
                // Refresh settings
                fetchSettings();
            } else {
                toast.error(result.message || 'Failed to fix browser');
            }
        } catch (error) {
            toast.error('Error fixing browser');
        } finally {
            setFixingBrowser(false);
        }
    };

    // Check if the error is a browser installation issue
    const isBrowserError = settings?.last_run_message?.includes('Executable doesn\'t exist') || 
                          settings?.last_run_message?.includes('playwright') ||
                          settings?.last_run_message?.includes('browser');

    const formatDateTime = (isoString) => {
        if (!isoString) return 'Never';
        const date = new Date(isoString);
        return date.toLocaleString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };

    const getStatusBadge = () => {
        if (settings?.is_running) {
            return (
                <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    Running
                </Badge>
            );
        }
        if (settings?.last_run_status === 'success') {
            return (
                <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Success
                </Badge>
            );
        }
        if (settings?.last_run_status === 'error') {
            return (
                <Badge 
                    className="bg-red-500/20 text-red-400 border-red-500/30 cursor-pointer hover:bg-red-500/30 transition-colors"
                    onClick={() => setShowErrorDetails(true)}
                    data-testid="error-badge"
                >
                    <XCircle className="w-3 h-3 mr-1" />
                    Error - Click for details
                </Badge>
            );
        }
        return (
            <Badge className="bg-[#333] text-[#888] border-[#444]">
                <AlertCircle className="w-3 h-3 mr-1" />
                Not Run Yet
            </Badge>
        );
    };

    const copyErrorToClipboard = () => {
        if (settings?.last_run_message) {
            navigator.clipboard.writeText(settings.last_run_message);
            toast.success('Error message copied to clipboard');
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-[#F51E1E]" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="max-w-4xl mx-auto space-y-8">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-white">Scraper Settings</h1>
                        <p className="text-[#A3A3A3] mt-1">Configure automated HRDRV opportunity scraping</p>
                    </div>
                    <Button
                        onClick={handleRunNow}
                        disabled={settings?.is_running || runningManual}
                        className="bg-[#F51E1E] hover:bg-[#D41212] text-white"
                        data-testid="run-now-btn"
                    >
                        {settings?.is_running ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Running...
                            </>
                        ) : (
                            <>
                                <Play className="w-4 h-4 mr-2" />
                                Run Now
                            </>
                        )}
                    </Button>
                </div>

                {/* Status Card */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-[#1A1A1A] border border-[#333] rounded-xl p-6"
                >
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-lg bg-[#F51E1E]/10 flex items-center justify-center">
                            <RefreshCw className="w-5 h-5 text-[#F51E1E]" />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-white">Scraper Status</h2>
                            <p className="text-sm text-[#666]">Current status and last run information</p>
                        </div>
                        <div className="ml-auto">
                            {getStatusBadge()}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Last Run */}
                        <div className="bg-[#262626] rounded-lg p-4">
                            <div className="flex items-center gap-2 text-[#888] mb-2">
                                <Clock className="w-4 h-4" />
                                <span className="text-sm">Last Run</span>
                            </div>
                            <p className="text-white font-medium">
                                {formatDateTime(settings?.last_run_at)}
                            </p>
                            {settings?.last_run_message && settings?.last_run_status === 'error' && (
                                <button 
                                    onClick={() => setShowErrorDetails(true)}
                                    className="text-xs text-red-400 mt-1 hover:text-red-300 transition-colors text-left flex items-center gap-1"
                                >
                                    <AlertCircle className="w-3 h-3" />
                                    <span className="truncate max-w-[180px]">{settings.last_run_message}</span>
                                </button>
                            )}
                            {settings?.last_run_message && settings?.last_run_status === 'success' && (
                                <p className="text-xs text-green-400 mt-1">
                                    {settings.last_run_message}
                                </p>
                            )}
                        </div>

                        {/* Next Run */}
                        <div className="bg-[#262626] rounded-lg p-4">
                            <div className="flex items-center gap-2 text-[#888] mb-2">
                                <Calendar className="w-4 h-4" />
                                <span className="text-sm">Next Scheduled Run</span>
                            </div>
                            <p className="text-white font-medium">
                                {settings?.enabled ? formatDateTime(settings?.next_run_at) : 'Disabled'}
                            </p>
                            {settings?.enabled && settings?.next_run_at && (
                                <p className="text-xs text-[#666] mt-1">
                                    Every {settings?.interval_hours} hour{settings?.interval_hours !== 1 ? 's' : ''}
                                </p>
                            )}
                        </div>

                        {/* Opportunities Found */}
                        <div className="bg-[#262626] rounded-lg p-4">
                            <div className="flex items-center gap-2 text-[#888] mb-2">
                                <CheckCircle2 className="w-4 h-4" />
                                <span className="text-sm">Last Run Results</span>
                            </div>
                            <p className="text-white font-medium">
                                {settings?.opportunities_found || 0} opportunities
                            </p>
                            <p className="text-xs text-[#666] mt-1">
                                Found and saved to database
                            </p>
                        </div>
                    </div>

                    {/* Error Details Alert - Shows inline when there's an error */}
                    {settings?.last_run_status === 'error' && settings?.last_run_message && (
                        <div className="mt-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                            <div className="flex items-start gap-3">
                                <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                    <p className="text-red-400 font-medium mb-1">Scraper Error</p>
                                    <p className="text-sm text-red-300/80 break-words whitespace-pre-wrap">
                                        {settings.last_run_message}
                                    </p>
                                    
                                    {/* Fix Browser Button - Show when it's a browser error */}
                                    {isBrowserError && (
                                        <div className="mt-4 pt-4 border-t border-red-500/20">
                                            <p className="text-sm text-red-300/60 mb-3">
                                                This error is caused by missing browser files. Click the button below to fix it.
                                            </p>
                                            <Button
                                                onClick={handleFixBrowser}
                                                disabled={fixingBrowser}
                                                className="bg-orange-500 hover:bg-orange-600 text-white"
                                                data-testid="fix-browser-btn"
                                            >
                                                {fixingBrowser ? (
                                                    <>
                                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                        Fixing Browser...
                                                    </>
                                                ) : (
                                                    <>
                                                        <Wrench className="w-4 h-4 mr-2" />
                                                        Fix Browser
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                                <button
                                    onClick={copyErrorToClipboard}
                                    className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-lg transition-colors"
                                    title="Copy error message"
                                >
                                    <Copy className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    )}
                </motion.div>

                {/* Settings Card */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="bg-[#1A1A1A] border border-[#333] rounded-xl p-6"
                >
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-lg bg-[#F51E1E]/10 flex items-center justify-center">
                            <Settings className="w-5 h-5 text-[#F51E1E]" />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-white">Schedule Configuration</h2>
                            <p className="text-sm text-[#666]">Adjust how often the scraper runs</p>
                        </div>
                    </div>

                    <div className="space-y-6">
                        {/* Enable/Disable Toggle */}
                        <div className="flex items-center justify-between p-4 bg-[#262626] rounded-lg">
                            <div>
                                <Label className="text-white font-medium">Enable Automated Scraping</Label>
                                <p className="text-sm text-[#666] mt-1">
                                    When enabled, the scraper will run automatically based on the schedule
                                </p>
                            </div>
                            <Switch
                                checked={enabled}
                                onCheckedChange={setEnabled}
                                data-testid="enabled-toggle"
                            />
                        </div>

                        {/* Interval Setting */}
                        <div className="p-4 bg-[#262626] rounded-lg">
                            <Label className="text-white font-medium">Scrape Interval (Hours)</Label>
                            <p className="text-sm text-[#666] mt-1 mb-4">
                                How often the scraper should check for new opportunities (1-168 hours)
                            </p>
                            <div className="flex items-center gap-4">
                                <Input
                                    type="number"
                                    min={1}
                                    max={168}
                                    value={intervalHours}
                                    onChange={(e) => setIntervalHours(Math.max(1, Math.min(168, parseInt(e.target.value) || 1)))}
                                    className="w-32 bg-[#1A1A1A] border-[#444] text-white"
                                    data-testid="interval-input"
                                />
                                <div className="flex gap-2">
                                    {[6, 12, 24, 48].map((hours) => (
                                        <Button
                                            key={hours}
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setIntervalHours(hours)}
                                            className={`border-[#444] ${intervalHours === hours ? 'bg-[#F51E1E] border-[#F51E1E] text-white' : 'text-[#888] hover:text-white'}`}
                                        >
                                            {hours}h
                                        </Button>
                                    ))}
                                </div>
                            </div>
                            <p className="text-xs text-[#666] mt-3">
                                {intervalHours === 24 ? 'Default: Once per day' : 
                                 intervalHours < 24 ? `${Math.round(24 / intervalHours)}x per day` :
                                 `Every ${Math.round(intervalHours / 24)} day${intervalHours >= 48 ? 's' : ''}`}
                            </p>
                        </div>

                        {/* Save Button */}
                        <div className="flex justify-end pt-4">
                            <Button
                                onClick={handleSave}
                                disabled={saving}
                                className="bg-[#F51E1E] hover:bg-[#D41212] text-white px-8"
                                data-testid="save-settings-btn"
                            >
                                {saving ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    'Save Settings'
                                )}
                            </Button>
                        </div>
                    </div>
                </motion.div>

                {/* Info Card */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-[#1A1A1A]/50 border border-[#333] rounded-xl p-4"
                >
                    <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-[#888] mt-0.5" />
                        <div className="text-sm text-[#888]">
                            <p className="font-medium text-[#A3A3A3] mb-1">About the Scraper</p>
                            <p>
                                The scraper automatically logs into HRDRV and collects all available sync opportunities. 
                                New opportunities are added to the database, and existing ones are updated. 
                                The scraper preserves your session to minimize login attempts.
                            </p>
                        </div>
                    </div>
                </motion.div>
            </div>

            {/* Error Details Modal */}
            <AnimatePresence>
                {showErrorDetails && settings?.last_run_message && (
                    <>
                        {/* Backdrop */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setShowErrorDetails(false)}
                            className="fixed inset-0 bg-black/70 z-50"
                        />
                        
                        {/* Modal */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="fixed inset-0 z-50 flex items-center justify-center p-4"
                        >
                            <div className="bg-[#1A1A1A] border border-[#333] rounded-xl w-full max-w-2xl max-h-[80vh] overflow-hidden" data-testid="error-modal">
                                {/* Modal Header */}
                                <div className="flex items-center justify-between px-6 py-4 border-b border-[#333]">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                                            <XCircle className="w-5 h-5 text-red-400" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-semibold text-white">Scraper Error Details</h3>
                                            <p className="text-sm text-[#666]">
                                                Last run: {formatDateTime(settings?.last_run_at)}
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setShowErrorDetails(false)}
                                        className="p-2 text-[#888] hover:text-white hover:bg-[#333] rounded-lg transition-colors"
                                    >
                                        <X className="w-5 h-5" />
                                    </button>
                                </div>
                                
                                {/* Modal Body */}
                                <div className="p-6 overflow-y-auto max-h-[50vh]">
                                    <div className="bg-[#0D0D0D] border border-[#333] rounded-lg p-4">
                                        <pre className="text-sm text-red-300 whitespace-pre-wrap font-mono break-words">
                                            {settings.last_run_message}
                                        </pre>
                                    </div>
                                    
                                    {/* Common Solutions */}
                                    <div className="mt-6">
                                        <h4 className="text-sm font-medium text-white mb-3">Common Solutions</h4>
                                        <ul className="space-y-2 text-sm text-[#A3A3A3]">
                                            <li className="flex items-start gap-2">
                                                <span className="text-[#F51E1E]">•</span>
                                                <span>If the error mentions "Playwright" or "browser", the browser may need to be reinstalled.</span>
                                            </li>
                                            <li className="flex items-start gap-2">
                                                <span className="text-[#F51E1E]">•</span>
                                                <span>If the error mentions "timeout", the HRDRV website may be slow or unavailable.</span>
                                            </li>
                                            <li className="flex items-start gap-2">
                                                <span className="text-[#F51E1E]">•</span>
                                                <span>If the error mentions "login" or "credentials", verify your HRDRV credentials are correct.</span>
                                            </li>
                                            <li className="flex items-start gap-2">
                                                <span className="text-[#F51E1E]">•</span>
                                                <span>Try clicking "Run Now" again - some errors are temporary.</span>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                                
                                {/* Modal Footer */}
                                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#333]">
                                    <Button
                                        variant="outline"
                                        onClick={copyErrorToClipboard}
                                        className="border-[#444] text-[#A3A3A3] hover:text-white"
                                    >
                                        <Copy className="w-4 h-4 mr-2" />
                                        Copy Error
                                    </Button>
                                    {isBrowserError && (
                                        <Button
                                            onClick={() => {
                                                setShowErrorDetails(false);
                                                handleFixBrowser();
                                            }}
                                            disabled={fixingBrowser}
                                            className="bg-orange-500 hover:bg-orange-600 text-white"
                                        >
                                            {fixingBrowser ? (
                                                <>
                                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                    Fixing...
                                                </>
                                            ) : (
                                                <>
                                                    <Wrench className="w-4 h-4 mr-2" />
                                                    Fix Browser
                                                </>
                                            )}
                                        </Button>
                                    )}
                                    <Button
                                        onClick={() => {
                                            setShowErrorDetails(false);
                                            handleRunNow();
                                        }}
                                        disabled={settings?.is_running}
                                        className="bg-[#F51E1E] hover:bg-[#D41212] text-white"
                                    >
                                        <Play className="w-4 h-4 mr-2" />
                                        Try Again
                                    </Button>
                                </div>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </Layout>
    );
}
